import { selectGameInfoError } from 'modules/overlay-v2/store/game-info/selectors';
import {
  selectIsBroadcastInitialized,
  selectIsMatchAvailable,
  selectIsMatchDataReady,
} from 'modules/overlay-v2/store/overlay/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { useAppSelector } from 'store/hooks';

export interface ControlPanelMessage {
  variant: 'info' | 'error';
  title: string;
  subtitle?: string;
}

/**
 * Почему панель сейчас нельзя показать — одним понятным сообщением.
 *
 * Панель открывают по ссылке в отдельном окне, без остального интерфейса, поэтому каждое
 * «не хватает данных» должно объяснять, что именно сделать: запустить матч в приложении,
 * выбрать шаблон у турнира, добавить табло. `undefined` — панель готова к работе.
 */
function useControlPanelReadiness(): ControlPanelMessage | undefined {
  const { organizerId, error: routeError } = useOverlayRouteParams();

  const isBroadcastInitialized = useAppSelector(selectIsBroadcastInitialized);
  const isMatchAvailable = useAppSelector(selectIsMatchAvailable);
  const isMatchDataReady = useAppSelector(selectIsMatchDataReady);
  const gameInfoError = useAppSelector(selectGameInfoError);

  const { championship } = useGameInfo();
  const { activeTemplate, isLoaded: isTemplateLoaded } = useTemplates();
  const { boards } = useBoards();

  const {
    data: feature,
    isLoading: isFeatureLoading,
    error: featureError,
  } = useFetchAvailableFeature({ userTournamentId: organizerId });

  if (routeError) return { variant: 'error', ...routeError };

  if (featureError) {
    return { variant: 'error', title: `Ошибка при получении подписки: ${(featureError as Error)?.message}` };
  }

  if (isFeatureLoading) {
    return {
      variant: 'info',
      title: 'Загрузка, подождите',
      subtitle: 'При плохом соединении загрузка может занимать несколько минут',
    };
  }

  if (!feature?.OBS_INTEGRATION) {
    return {
      variant: 'error',
      title: 'Подписка на OBS-интеграцию не активирована',
      subtitle: 'Панель управления входит в платную трансляцию — обратитесь в поддержку ImGame.',
    };
  }

  if (!isBroadcastInitialized) {
    return { variant: 'info', title: 'Подключаемся к устройству…' };
  }

  if (isMatchAvailable === false) {
    return {
      variant: 'info',
      title: 'На устройстве нет запущенного матча',
      subtitle:
        'Запустите матч в мобильном приложении ImGame — панель подхватит его сама, перезагружать страницу не нужно. ' +
        'Если матч уже идёт, проверьте, что в приложении выбрано то же устройство, что и в этой ссылке.',
    };
  }

  if (gameInfoError) {
    return { variant: 'error', title: `Ошибка при получении информации о матче: ${gameInfoError}` };
  }

  if (!isMatchDataReady) {
    return { variant: 'info', title: 'Загрузка данных матча…' };
  }

  if (!championship?.overlayId) {
    return {
      variant: 'error',
      title: `У турнира «${championship?.name ?? 'без названия'}» не выбран шаблон табло`,
      subtitle: 'Откройте «Турниры» → нужный турнир → вкладка «Основное» → поле «Шаблон» и выберите шаблон.',
    };
  }

  if (!isTemplateLoaded) {
    return { variant: 'info', title: 'Загрузка шаблона…' };
  }

  if (boards.length === 0) {
    return {
      variant: 'error',
      title: `В шаблоне «${activeTemplate?.name ?? 'без названия'}» нет ни одного табло`,
      subtitle: 'Откройте «Шаблоны» → этот шаблон и добавьте табло — управлять пока нечем.',
    };
  }

  return undefined;
}

export default useControlPanelReadiness;
